import React, { useState, useEffect } from "react";
import {
  Box,
  Snackbar,
  Alert,
  Skeleton,
  Grid,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../config";
import { VehicleAddDetails } from "./VehicleAddDetails";
import { VehicleDocumentManager } from "./VehicleDocumentManager";
import SubTitle from "../common/SubTitle";
import { isRouteEyeAdmin } from "../../utils/isRouteEyeAdmin";
import deviceIds from "../../deviceIds.json";
import {
  buildVehicleScheduleConflictMessage,
  getGenericApiErrorMessage,
} from "../../utils/scheduleUi";
import { buildAssignDriverVehiclePayload } from "../../utils/driverPayloads";

export default function VehicleAddMain({
  embedded = false,
  embeddedVehicleId,
  onCancel,
  onComplete,
  onDelete,
}) {
  const { t } = useTranslation();
  const [vehicleFormData, setVehicleFormData] = useState({
    device_id: "",
    name: "",
    VehicleNumber: "",
    seatCapacity: "",
    description: "...",
    ownershipType: "ORGANIZATION",
    contractorName: "",
    contractorPhone: "",
    contractorEmail: "",
  });

  const [color, setColor] = useState("#00FF00");
  const [selectedImage, setSelectedImage] = useState(null);
  const [documents, setDocuments] = useState([]); // Store filtered documents
  const [routes, setRoutes] = useState([]);
  const [sites, setSites] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [errors, setErrors] = useState({});
  const [createdVehicleId, setCreatedVehicleId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isDetailsFormValid, setIsDetailsFormValid] = useState(false);
  const [detailErrors, setDetailErrors] = useState({});
  const [assignedDeviceIds, setAssignedDeviceIds] = useState([]);
  const [initialVehicleName, setInitialVehicleName] = useState("Vehicle");

  // Track original assignment values for comparison during updates
  const [originalAssignments, setOriginalAssignments] = useState({
    routeId: null,
    driverId: null,
    siteId: null,
    attendantId: null,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const normalizedStateId =
    typeof location.state === "object"
      ? location.state?.id || location.state?.vehicleId || null
      : location.state;
  const vehicleId =
    embeddedVehicleId || normalizedStateId || id || createdVehicleId || null;
  const isEditing = Boolean(embeddedVehicleId || normalizedStateId || id);
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  const path =
    id || vehicleId
      ? `${orgName || "organization"} > Vehicles > ${initialVehicleName}`
      : `${orgName || "organization"} > Vehicles > Create`;
  const [isLoading, setIsLoading] = useState(isEditing);

  const handleValidationChange = (isValid, errors) => {
    setIsDetailsFormValid(isValid);
    setDetailErrors(errors);
    console.log("VehicleAddMain Validation Change:", { isValid, errors });
  };

  const normalizeIdValue = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  };

  const toOptionalValue = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    return value;
  };

  const toOptionalNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? undefined : numericValue;
  };

  const normalizeEnumValue = (value) => {
    if (typeof value !== "string") {
      return "";
    }

    return value.trim().toUpperCase();
  };

  const formatAirConditionForForm = (value) => {
    if (value === true || value === 1) {
      return "Yes";
    }

    if (value === false || value === 0) {
      return "No";
    }

    if (typeof value === "string") {
      const normalizedValue = value.trim().toLowerCase();

      if (["yes", "true", "1"].includes(normalizedValue)) {
        return "Yes";
      }

      if (["no", "false", "0"].includes(normalizedValue)) {
        return "No";
      }
    }

    return "";
  };

  const normalizeAirConditionForApi = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === "boolean") {
      return value;
    }

    if (value === 1 || value === 0) {
      return Boolean(value);
    }

    if (typeof value === "string") {
      const normalizedValue = value.trim().toLowerCase();

      if (["yes", "true", "1"].includes(normalizedValue)) {
        return true;
      }

      if (["no", "false", "0"].includes(normalizedValue)) {
        return false;
      }
    }

    return undefined;
  };

  const normalizeMechanicForApi = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === "string") {
      return value.trim() || undefined;
    }

    if (typeof value === "object") {
      return (
        value.name ||
        value.label ||
        value.value ||
        toOptionalValue(String(value))
      );
    }

    return toOptionalValue(String(value));
  };

  const fetchRoutesForSite = async (organizationId, siteId) => {
    if (!organizationId || !siteId) {
      return [];
    }

    try {
      const response = await axios.get(`${config.baseURL}/site/organization/route`, {
        params: { organizationId, siteId },
        withCredentials: true,
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching routes for site:", error);
      return [];
    }
  };

  const fetchSites = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/site/organization`, {
        params: { organizationId },
        withCredentials: true,
      });
      console.log("Sites response:", response.data);
      if (response.data && Array.isArray(response.data)) {
        setSites(response.data);
      } else {
        setSites([]);
        setAlert({
          open: true,
          message: t("No sites found"),
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSites([]);
    }
  };

  const fetchDrivers = async (organizationId) => {
    try {
      const response = await axios.get(
        `${config.baseURL}/driver/organization`,
        {
          params: { organizationId },
          withCredentials: true,
        }
      );
      console.log("Drivers response:", response.data);
      if (response.data && Array.isArray(response.data)) {
        setDrivers(response.data);
      } else {
        setDrivers([]);
        setAlert({
          open: true,
          message: t("No drivers found"),
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setAlert({
        open: true,
        message: t("Failed to fetch drivers"),
        severity: "error",
      });
      setDrivers([]);
    }
  };

  const fetchAttendants = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/employee/organization`, {
        params: { organizationId },
        withCredentials: true,
      });
      setAttendants(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching attendants:", error);
      setAttendants([]);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const userStateString = localStorage.getItem("userState");
        if (!userStateString) {
          setErrors({ general: "User state not found in localStorage" });
          return;
        }
        const userState = JSON.parse(userStateString);
        console.log("User state:", userState);

        const organizationId = userState.user.organizationId;
        if (!organizationId) {
          setErrors({ general: "Organization ID not found" });
          return;
        }

        setOrgId(organizationId);
        console.log("Organization ID:", organizationId);

        if (isMounted) {
          await Promise.all([
            fetchSites(organizationId),
            fetchDrivers(organizationId),
            fetchAttendants(organizationId),
          ]);
        }

        if (isEditing && vehicleId && isMounted) {
          await fetchVehicleData(vehicleId, organizationId);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error initializing data:", error);
          setErrors({ general: "Failed to load initial data" });
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [isEditing, vehicleId]);

  useEffect(() => {
    const siteId = vehicleFormData.sites;
    if (!orgId || !siteId) {
      setRoutes([]);
      return undefined;
    }

    let isCurrent = true;
    fetchRoutesForSite(orgId, siteId).then((siteRoutes) => {
      if (!isCurrent) return;
      setRoutes(siteRoutes);
      setVehicleFormData((prev) => {
        if (String(prev.sites || "") !== String(siteId)) return prev;
        if (!prev.routes) return prev;
        const routeStillInsideSite = siteRoutes.some(
          (route) => String(route.id) === String(prev.routes),
        );
        return routeStillInsideSite ? prev : { ...prev, routes: "" };
      });
    });

    return () => {
      isCurrent = false;
    };
  }, [orgId, vehicleFormData.sites]);

  const filterMostRecentDocuments = (docs) => {
    // Group documents by name
    const groupedDocs = docs.reduce((acc, doc) => {
      if (!acc[doc.name]) {
        acc[doc.name] = [];
      }
      acc[doc.name].push(doc);
      return acc;
    }, {});

    // For each group, sort by createdAt (descending) and take the most recent
    const filteredDocs = Object.values(groupedDocs).map((group) => {
      return group.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
    });

    return filteredDocs;
  };

  const fetchVehicleData = async (id, organizationId) => {
    try {
      setIsLoading(true);
      const vehicleResponse = await axios.get(`${config.baseURL}/vehicle/id`, {
        params: { vehicleId: id, organizationId },
        withCredentials: true,
      });
      console.log("Vehicle data response:", vehicleResponse.data);
      if (vehicleResponse.data && vehicleResponse.data.vehicle) {
        const vehicleData = vehicleResponse.data.vehicle;
        const routeId = normalizeIdValue(
          vehicleData.routeId ?? vehicleData.route?.id
        );
        const driverId = normalizeIdValue(vehicleData.driver?.id);
        const siteId = normalizeIdValue(
          vehicleData.siteId ?? vehicleData.site?.id
        );
        const attendantId = normalizeIdValue(
          vehicleData.employees?.[0]?.id ?? vehicleData.employee?.id
        );
        const reRegistrationDate =
          vehicleData.reRegistration || vehicleData.reregistrationDueDate || "";
        const nextEmissionTestDate =
          vehicleData.nextEmissionTestDate ||
          vehicleData.emissionTestDueDate ||
          "";

        setInitialVehicleName(vehicleData.name || "Vehicle");
        setOriginalAssignments({
          routeId,
          driverId,
          siteId,
          attendantId,
        });
        setColor(vehicleData.vehicle_color || "#00FF00");

        setVehicleFormData({
          ...vehicleData,
          organizationId: vehicleData.organizationId || organizationId,
          device_id: vehicleData.device_id || vehicleData.deviceId || "",
          routes: routeId || "",
          driverId: driverId || "",
          sites: siteId || "",
          attendantId: attendantId || "",
          vehicleType: normalizeEnumValue(
            vehicleData.VehicleType || vehicleData.vehicleType
          ),
          bodyType: normalizeEnumValue(vehicleData.bodyType),
          fuelType: normalizeEnumValue(vehicleData.fuelType),
          airCondition: formatAirConditionForForm(vehicleData.airCondition),
          seatCapacity:
            vehicleData.seatCapacity ?? vehicleData.vehicleSeatCapacity ?? "",
          reRegistration: reRegistrationDate,
          reregistrationDueDate: reRegistrationDate,
          nextEmissionTestDate: nextEmissionTestDate,
          emissionTestDueDate: nextEmissionTestDate,
          ownershipType: ["CONTRACTOR", "THIRD_PARTY", "THIRD PARTY"].includes(
            String(vehicleData.ownershipType || "").toUpperCase()
          )
            ? "CONTRACTOR"
            : "ORGANIZATION",
          contractorName: vehicleData.contractorName || "",
          contractorPhone: vehicleData.contractorPhone || "",
          contractorEmail: vehicleData.contractorEmail || "",
        });

        // Filter documents to keep only the most recent for each type
        const allDocuments = vehicleResponse.data.vehicle.documents || [];
        const filteredDocuments = filterMostRecentDocuments(allDocuments);

        // Extract the most recent VehicleImage
        const vehicleImage = filteredDocuments.find(
          (doc) => doc.name === "VehicleImage"
        );
        const otherDocuments = filteredDocuments.filter(
          (doc) => doc.name !== "VehicleImage"
        );

        // Set the profile image
        if (vehicleImage) {
          setSelectedImage({
            location: `${
              config.baseURL
            }/upload/image?fileName=${encodeURIComponent(
              vehicleImage.location
            )}`,
            isExisting: true,
            fileName: vehicleImage.location,
            type: "profile",
            id: vehicleImage.id,
          });
        } else {
          setSelectedImage(null);
        }

        // Set the remaining documents for VehicleDocumentManager
        setDocuments(otherDocuments);
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      setAlert({
        open: true,
        message: t("Failed to load vehicle data"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const assignVehicleToRoute = async (vehicleId, routeId) => {
    if (!routeId) return;
    try {
      console.log("Assigning vehicle to route:", { routeId, vehicleId });
      // Added proper JSON stringify for debugging
      console.log(
        "Assignment payload:",
        JSON.stringify({
          routeId: routeId,
          vehicleId: vehicleId,
        })
      );
      const response = await axios.patch(
        `${config.baseURL}/vehicle/route`,
        {
          routeId: routeId,
          vehicleId: vehicleId,
        },
        { withCredentials: true }
      );
      console.log("Route assignment response:", response.data);
      console.log("Vehicle assigned to route successfully");
    } catch (error) {
      console.error("Error assigning vehicle to route:", error);
      throw new Error("Failed to assign vehicle to route");
    }
  };

  const assignVehicleToDriver = async (vehicleId, driverId) => {
    if (!driverId) return;

    try {
      console.log("Assigning vehicle to driver:", { driverId, vehicleId });
      await axios.put(
        `${config.baseURL}/driver/vehicle`,
        buildAssignDriverVehiclePayload(driverId, vehicleId),
        { withCredentials: true }
      );
      console.log("Vehicle assigned to driver successfully");
    } catch (error) {
      console.error("Error assigning vehicle to driver:", error);
      throw new Error("Failed to assign vehicle to driver");
    }
  };

  const assignVehicleToAttendant = async (vehicleId, attendantId) => {
    if (!attendantId) return;

    try {
      const userId = JSON.parse(localStorage.getItem("userState"))?.user?.id;
      await axios.patch(
        `${config.baseURL}/employee/vehicle`,
        {
          employeeId: attendantId,
          vehicleId,
          performedByUserId: userId,
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error assigning vehicle to attendant:", error);
      throw new Error("Failed to assign vehicle to attendant");
    }
  };

  const assignVehicleToSite = async (vehicleId, siteId) => {
    if (!siteId) return;

    try {
      console.log("Assigning vehicle to site:", {
        siteId,
        vehicleIds: [vehicleId],
      });
      await axios.put(
        `${config.baseURL}/site/vehicles`,
        {
          siteId: siteId,
          vehicleIds: [vehicleId],
        },
        { withCredentials: true }
      );
      console.log("Vehicle assigned to site successfully");
    } catch (error) {
      console.error("Error assigning vehicle to site:", error);
      throw new Error("Failed to assign vehicle to site");
    }
  };

  // Site first, then route, then driver. Site assignment clears the previous
  // route, so the selected route has to be applied after the site.
  const handleAssignments = async (vehicleId) => {
    const currentRouteId = normalizeIdValue(vehicleFormData.routes);
    const currentDriverId = normalizeIdValue(vehicleFormData.driverId);
    const currentSiteId = normalizeIdValue(vehicleFormData.sites);
    const currentAttendantId = normalizeIdValue(vehicleFormData.attendantId);
    const siteChanged = currentSiteId !== originalAssignments.siteId;
    const routeChanged = currentRouteId !== originalAssignments.routeId;
    const driverChanged = currentDriverId !== originalAssignments.driverId;
    const attendantChanged = currentAttendantId !== originalAssignments.attendantId;

    if (siteChanged && currentSiteId) {
      await assignVehicleToSite(vehicleId, currentSiteId);
    }

    if ((routeChanged || siteChanged) && currentRouteId) {
      await assignVehicleToRoute(vehicleId, currentRouteId);
    }

    if (driverChanged && currentDriverId) {
      await assignVehicleToDriver(vehicleId, currentDriverId);
    }

    if (attendantChanged && currentAttendantId) {
      await assignVehicleToAttendant(vehicleId, currentAttendantId);
    }
  };

  const handleSchedulesSelected = async (scheduleIds, vId) => {
    try {
      const selectedScheduleIds = Object.keys(scheduleIds)
        .filter((scheduleId) => scheduleIds[scheduleId])
        .map(Number);

      if (selectedScheduleIds.length === 0) {
        return;
      }

      await axios.patch(
        `${config.baseURL}/vehicle/schedule`,
        {
          scheduleIds: selectedScheduleIds,
          vehicleId: vId,
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error updating schedules:", error);
      throw error;
    }
  };

  const formatDateToISO = (dateValue) => {
    if (!dateValue) return "";

    // If it's already a Date object, convert to ISO string
    if (dateValue instanceof Date) {
      return dateValue.toISOString();
    }

    // If it's a string, check if it already has time
    if (typeof dateValue === "string") {
      if (dateValue.includes("T")) return dateValue;
      return `${dateValue}T00:00:00.000Z`;
    }

    return "";
  };
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [/*'device_id',*/ "name", "VehicleNumber"]; // Remove 'device_id'
    requiredFields.forEach((field) => {
      if (
        !vehicleFormData[field] ||
        (typeof vehicleFormData[field] === "string" &&
          vehicleFormData[field].trim().length === 0)
      ) {
        newErrors[field] = `${
          field === "VehicleNumber"
            ? "Vehicle Number"
            : field
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())
        } is required`;
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setDetailErrors(newErrors);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitVehicleDetails = async () => {
    if (!isDetailsFormValid) {
      setAlert({
        open: true,
        message: t("Please correct all validation errors before submitting"),
        severity: "error",
      });
      return false;
    }

    // Only merge relevant errors
    // const relevantErrors = {};
    // Object.keys(detailErrors).forEach((key) => {
    //   if (["name", "VehicleNumber"].includes(key)) {
    //     // Remove 'device_id'
    //     relevantErrors[key] = detailErrors[key];
    //   }
    // });
    // setErrors((prev) => ({ ...prev, ...detailErrors }));

    // if (!validateForm()) {
    //   // setAlert({
    //   //   open: true,
    //   //   message: t("Please fill in all required fields"),
    //   //   severity: "error",
    //   // });
    //   setDetailErrors((prev) => ({
    //     ...prev,
    //     name: !vehicleFormData.name ? "Vehicle Name is required" : "",
    //     VehicleNumber: !vehicleFormData.VehicleNumber
    //       ? "Vehicle Number is required"
    //       : "",
    //   }));
    //   return false;
    // }

    const newErrors = {};

    if (!vehicleFormData.name || vehicleFormData.name.trim() === "") {
      newErrors.name = "Vehicle Name is required";
    }
    if (
      !vehicleFormData.VehicleNumber ||
      vehicleFormData.VehicleNumber.trim() === ""
    ) {
      newErrors.VehicleNumber = "Vehicle Number is required";
    }

    // Set the errors which will trigger the UI to show red borders and messages
    if (Object.keys(newErrors).length > 0) {
      setDetailErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));
      return false;
    }

    // Validate the form
    if (!validateForm()) {
      return false;
    }

    const normalizedDeviceId = toOptionalValue(
      vehicleFormData.device_id || vehicleFormData.deviceId
    );

    if (isEditing && !normalizedDeviceId) {
      setDetailErrors((prev) => ({
        ...prev,
        device_id: "Device ID is required",
      }));
      return false;
    }

    setIsSubmitting(true);

    try {
      let assignmentWarningMessage = "";
      const normalizedVehicleType = toOptionalValue(
        normalizeEnumValue(vehicleFormData.vehicleType)
      );
      const normalizedFuelType = toOptionalValue(
        normalizeEnumValue(vehicleFormData.fuelType)
      );
      const normalizedBodyType = toOptionalValue(
        normalizeEnumValue(vehicleFormData.bodyType)
      );
      const vehiclePayload = {
        // ── Core fields ──────────────────────────────────────────────────────
        name: vehicleFormData.name,
        VehicleNumber: vehicleFormData.VehicleNumber,
        organizationId: toOptionalValue(vehicleFormData.organizationId || orgId),
        description: toOptionalValue(vehicleFormData.description),
        status: toOptionalValue(vehicleFormData.status),

        // ── Vehicle Info ─────────────────────────────────────────────────────
        Make: toOptionalValue(vehicleFormData.Make),
        Model: toOptionalValue(vehicleFormData.Model),
        VehicleType: normalizedVehicleType,
        bodyType: normalizedBodyType,
        fuelType: normalizedFuelType,
        airCondition: normalizeAirConditionForApi(
          vehicleFormData.airCondition
        ),
        Year: toOptionalValue(vehicleFormData.Year),
        seatCapacity: toOptionalNumber(vehicleFormData.seatCapacity),
        chassisNumber: toOptionalValue(vehicleFormData.chassisNumber),
        vehicle_color: toOptionalValue(color),
        ownershipType:
          String(vehicleFormData.ownershipType || "ORGANIZATION").toUpperCase() ===
          "CONTRACTOR"
            ? "CONTRACTOR"
            : "ORGANIZATION",
        contractorName:
          String(vehicleFormData.ownershipType || "").toUpperCase() ===
          "CONTRACTOR"
            ? toOptionalValue(vehicleFormData.contractorName)
            : null,
        contractorPhone:
          String(vehicleFormData.ownershipType || "").toUpperCase() ===
          "CONTRACTOR"
            ? toOptionalValue(vehicleFormData.contractorPhone)
            : null,
        contractorEmail:
          String(vehicleFormData.ownershipType || "").toUpperCase() ===
          "CONTRACTOR"
            ? toOptionalValue(vehicleFormData.contractorEmail)
            : null,

        // ── Assignments ──────────────────────────────────────────────────────

        // ── Maintenance ──────────────────────────────────────────────────────
        lastServiceDate:
          formatDateToISO(vehicleFormData.lastServiceDate) || undefined,
        serviceDueDate:
          formatDateToISO(vehicleFormData.serviceDueDate) || undefined,
        mechanic: normalizeMechanicForApi(vehicleFormData.mechanic),
        mechanicPhoneNumber: toOptionalValue(
          vehicleFormData.mechanicPhoneNumber
        ),

        // ── Compliance ───────────────────────────────────────────────────────
        insuranceProvider: toOptionalValue(vehicleFormData.insuranceProvider),
        insurancePolicyNumber: toOptionalValue(
          vehicleFormData.insurancePolicyNumber
        ),
        insurancePolicyExpiry:
          formatDateToISO(vehicleFormData.insurancePolicyExpiry) || undefined,
        reRegistration:
          formatDateToISO(
            vehicleFormData.reRegistration ||
              vehicleFormData.reregistrationDueDate
          ) || undefined,
        lastEmissionTestDate:
          formatDateToISO(vehicleFormData.lastEmissionTestDate) || undefined,
        nextEmissionTestDate:
          formatDateToISO(
            vehicleFormData.nextEmissionTestDate ||
              vehicleFormData.emissionTestDueDate
          ) || undefined,

        // ── Audit ────────────────────────────────────────────────────────────
      };

      let response;
      if (isEditing) {
        console.log("Updating vehicle with ID:", vehicleId);
        vehiclePayload.id = normalizeIdValue(vehicleId);
        vehiclePayload.device_id = normalizedDeviceId;
        response = await axios.put(
          `${config.baseURL}/vehicle`,
          vehiclePayload,
          { withCredentials: true }
        );
      } else {
        console.log("Creating new vehicle");
        if (normalizedDeviceId) {
          vehiclePayload.device_id = normalizedDeviceId;
        }
        response = await axios.post(
          `${config.baseURL}/vehicle`,
          vehiclePayload,
          { withCredentials: true }
        );
      }

      const savedVehicle =
        response.data?.updatedVehicle || response.data?.vehicle || null;
      const newVehicleId = normalizeIdValue(savedVehicle?.id || vehicleId);
      console.log("Vehicle ID after submit:", newVehicleId);
      setCreatedVehicleId(newVehicleId);

      if (selectedImage && selectedImage instanceof File) {
        // Wait a moment to ensure vehicle is available in backend
        if (!isEditing) {
          await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay
        }
        const imageFormData = new FormData();
        imageFormData.append("image", selectedImage);
        try {
          const response = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent(
              "VehicleImage"
            )}&associateEntityId=${newVehicleId}&associatedWith=Vehicle&type=profile`,
            imageFormData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          setSelectedImage({
            location: `${
              config.baseURL
            }/upload/image?fileName=${encodeURIComponent(
              response.data.location
            )}`,
            isExisting: true,
            fileName: response.data.location,
            type: "profile",
            id: response.data.id,
          });
        } catch (imageError) {
          console.error("Vehicle image upload failed:", imageError);
          setAlert({
            open: true,
            message: t("Vehicle saved, but image upload failed"),
            severity: "warning",
          });
        }
      }

      // Handle assignments differently for create vs update
      try {
        if (isEditing) {
          // For updates, only update assignments that have changed
          await handleAssignments(newVehicleId);
        } else {
          // Site first, then route, then driver.
          if (vehicleFormData.sites) {
            await assignVehicleToSite(newVehicleId, vehicleFormData.sites);
          }

          if (vehicleFormData.routes) {
            await assignVehicleToRoute(newVehicleId, vehicleFormData.routes);
          }

          if (vehicleFormData.driverId) {
            await assignVehicleToDriver(newVehicleId, vehicleFormData.driverId);
          }

          if (vehicleFormData.attendantId) {
            await assignVehicleToAttendant(newVehicleId, vehicleFormData.attendantId);
          }
        }

        // Handle schedules for both create and update
        if (Object.keys(selectedSchedules).length > 0) {
          await handleSchedulesSelected(selectedSchedules, newVehicleId);
        }
      } catch (assignmentError) {
        console.error("Error in assignment operations:", assignmentError);
        assignmentWarningMessage =
          assignmentError?.response?.data?.code === "SCHEDULE_CONFLICT"
            ? buildVehicleScheduleConflictMessage(assignmentError, {
                vehicleName: vehicleFormData.name,
                vehicleNumber: vehicleFormData.VehicleNumber,
                fallbackMessage: t(
                  "Vehicle saved, but there were errors with some assignments"
                ),
              })
            : getGenericApiErrorMessage(
                assignmentError,
                t("Vehicle saved, but there were errors with some assignments")
              );
      }

      // Refresh documents after submission

      await fetchVehicleData(newVehicleId, orgId);

      setAlert({
        open: true,
        message:
          assignmentWarningMessage || t("Vehicle and image saved successfully"),
        severity: assignmentWarningMessage ? "warning" : "success",
      });
      return newVehicleId;
    } catch (error) {
      console.error("Submission failed:", error);
      console.error(
        "Response data:",
        error.response ? error.response.data : "No response data"
      );
      setAlert({
        open: true,
        message: t("Failed to save vehicle or image. Please try again."),
        severity: "error",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (embedded) {
      onCancel?.();
      return;
    }

    if (vehicleId) {
      navigate("/Vehicle");
    } else {
      navigate(-1);
    }
  };

  const handleDocumentsComplete = () => {
    if (embedded) {
      onComplete?.();
      return;
    }

    navigate("/Vehicle");
    setAlert({
      open: true,
      message: t("Vehicle, image, and documents saved successfully"),
      severity: "success",
    });
  };

  useEffect(() => {
    if (isRouteEyeAdmin()) {
      axios
        .get(`${config.baseURL}/vehicle`, { withCredentials: true })
        .then((response) => {
          let vehicles = [];
          if (Array.isArray(response.data)) {
            vehicles = response.data;
          } else if (Array.isArray(response.data.vehicles)) {
            vehicles = response.data.vehicles;
          }
          const ids = vehicles.map((v) => v.device_id).filter(Boolean);
          setAssignedDeviceIds(ids);
        })
        .catch((error) => {
          console.error(
            "Failed to fetch vehicles for device ID filtering",
            error
          );
        });
    }
  }, []);

  const availableDeviceIds = deviceIds.deviceIds.filter(
    (id) => !assignedDeviceIds.includes(id) || vehicleFormData.device_id === id
  );
  console.log(availableDeviceIds, "availableDeviceIds");

  return (
    <>
      {!embedded && (
        <SubTitle
          title={vehicleId ? "Update Vehicle" : "Create Vehicles"}
          path={path}
        />
      )}

      <Box sx={{ mb: 4 }}>
        {isLoading ? (
          // Skeleton loader for VehicleAddDetails
          <Box sx={{ p: 4 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={60}
              sx={{ mb: 3, borderRadius: 2 }}
            />
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={400}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={350}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        ) : (
          <VehicleAddDetails
            vehicleFormData={vehicleFormData}
            setVehicleFormData={setVehicleFormData}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            routes={routes}
            drivers={drivers}
            sites={sites}
            attendants={attendants}
            isEditing={isEditing}
            selectedSchedules={selectedSchedules}
            setSelectedSchedules={setSelectedSchedules}
            errors={detailErrors}
            setErrors={setDetailErrors}
            onValidationChange={handleValidationChange}
            deviceId={vehicleFormData.device_id}
            setDeviceId={(id) =>
              setVehicleFormData({ ...vehicleFormData, device_id: id })
            }
            showDeviceId={isRouteEyeAdmin()}
            allowedDeviceIds={availableDeviceIds}
            setColor={setColor}
            color={color}
            embedded={embedded}
          />
        )}
      </Box>
      <Box sx={{ mb: 4 }}>
        {!isLoading && (
          <VehicleDocumentManager
            errors={errors}
            vehicleId={isEditing ? vehicleId : createdVehicleId}
            documents={documents}
            setDocuments={setDocuments}
            isEditing={isEditing}
            onComplete={handleDocumentsComplete}
            orgId={orgId}
            onSubmit={handleSubmitVehicleDetails}
            onCancel={handleCancel}
            isDetailsFormValid={isDetailsFormValid || isEditing}
            embedded={embedded}
            showDeleteButton={embedded && isEditing && Boolean(onDelete)}
            onDelete={onDelete}
            saveButtonText={isEditing ? t("Update") : t("Save")}
          />
        )}
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}
